import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { RefreshCw, X, ChevronLeft, ChevronRight, Link2, Link2Off } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// pdfjs worker — same CDN pin as PdfPreviewPane so no duplicate downloads.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * DraftCompareModal — side-by-side PDF preview for two Design Drafts on
 * the same variant + roof_key + reference appraisal.
 *
 * Uses the same `/api/reports/detailed-roof-appraisal/{id}?draft_id=…`
 * endpoint the Studio's main preview already relies on — only the
 * `draft_id` query param differs between the two panes, so both renders
 * come from a single backend endpoint with zero new plumbing.
 *
 * Sync-scroll:
 *   • ON  → both panes share a page number; clicking Next/Prev or a
 *          thumbnail on either side flips both.
 *   • OFF → each pane has its own page cursor.
 *
 * Props:
 *   • drafts        — full list of design_drafts for the current variant
 *   • defaultA/B    — starting draft ids for the left/right panes
 *   • buildUrl      — (draftId) => string; produced by ScriptAuditPage so
 *                     variant/roofKey/refAppraisal/token stay in sync
 *   • variantLabel  — human-readable header string
 *   • onClose       — called when the operator dismisses the modal
 */
export default function DraftCompareModal({
  drafts,
  defaultA,
  defaultB,
  buildUrl,
  variantLabel,
  onClose,
}) {
  const [draftAId, setDraftAId] = useState(defaultA || (drafts[0] && drafts[0]._id) || '');
  const [draftBId, setDraftBId] = useState(defaultB || (drafts[1] && drafts[1]._id) || '');
  const [syncScroll, setSyncScroll] = useState(true);
  // iter298d — bust key mirrors the pattern from PdfPreviewPane so that
  // hitting "↻ Refresh" on either side reloads the PDF instead of hitting
  // the browser cache.
  const [bustA, setBustA] = useState(0);
  const [bustB, setBustB] = useState(0);

  // Shared page cursor (only active when syncScroll = true). When sync
  // is off, each pane owns its own pageNumber state.
  const [sharedPage, setSharedPage] = useState(1);
  const [aPage, setAPage] = useState(1);
  const [bPage, setBPage] = useState(1);
  const pageA = syncScroll ? sharedPage : aPage;
  const pageB = syncScroll ? sharedPage : bPage;
  const setPageA = syncScroll ? setSharedPage : setAPage;
  const setPageB = syncScroll ? setSharedPage : setBPage;

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const draftA = drafts.find(d => d._id === draftAId);
  const draftB = drafts.find(d => d._id === draftBId);
  const urlA = draftA ? buildUrl(draftA._id) : '';
  const urlB = draftB ? buildUrl(draftB._id) : '';

  return (
    <div
      data-testid="draft-compare-modal"
      className="fixed inset-0 z-50 bg-slate-900/85 backdrop-blur-sm flex items-stretch"
    >
      <div className="flex-1 flex flex-col bg-[#F5F5F0]">
        {/* Header */}
        <div className="px-4 py-3 border-b-2 border-purple-500 bg-white flex items-center gap-3 flex-shrink-0">
          <span className="text-lg">🆚</span>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700">
              Compare Drafts · {variantLabel}
            </div>
            <div className="text-[10px] text-slate-500">
              Same reference appraisal on both sides · {syncScroll ? 'pages synced' : 'independent pages'}
            </div>
          </div>
          <button
            data-testid="compare-sync-toggle"
            onClick={() => setSyncScroll(s => !s)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold border transition-colors ${syncScroll ? 'bg-purple-600 text-white border-purple-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
            title="When enabled, both panes always show the same page number"
          >
            {syncScroll ? <Link2 size={12} /> : <Link2Off size={12} />}
            {syncScroll ? 'Sync ON' : 'Sync OFF'}
          </button>
          <button
            data-testid="compare-close"
            onClick={onClose}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
          >
            <X size={12} /> Close
          </button>
        </div>

        {/* Split preview area */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-2 p-2">
          <ComparePane
            testId="compare-pane-a"
            label="Draft A"
            drafts={drafts}
            selectedId={draftAId}
            onSelect={setDraftAId}
            url={urlA}
            bustKey={bustA}
            onRefresh={() => setBustA(b => b + 1)}
            pageNumber={pageA}
            onPageChange={setPageA}
          />
          <ComparePane
            testId="compare-pane-b"
            label="Draft B"
            drafts={drafts}
            selectedId={draftBId}
            onSelect={setDraftBId}
            url={urlB}
            bustKey={bustB}
            onRefresh={() => setBustB(b => b + 1)}
            pageNumber={pageB}
            onPageChange={setPageB}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * ComparePane — compact single-column PDF viewer for the compare modal.
 * Header holds the draft selector + refresh; body shows a horizontal
 * thumbnail strip and the big rendered page. Uses the same react-pdf
 * setup as PdfPreviewPane but omits the ref-appraisal / focused-preview
 * chrome to keep the two panes clean.
 */
function ComparePane({
  testId,
  label,
  drafts,
  selectedId,
  onSelect,
  url,
  bustKey,
  onRefresh,
  pageNumber,
  onPageChange,
}) {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const docOptions = useMemo(() => ({}), []);

  const onDocLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setLoadError(null);
    // Clamp page number if the other side has more/fewer pages.
    if (pageNumber > n) onPageChange(1);
  }, [pageNumber, onPageChange]);

  const onDocLoadError = useCallback((err) => {
    setLoadError(err?.message || String(err) || 'PDF failed to load');
  }, []);

  const selectedDraft = drafts.find(d => d._id === selectedId);

  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-300 rounded-md overflow-hidden flex flex-col min-h-0"
    >
      {/* Sub-header: draft dropdown + refresh */}
      <div className="px-2.5 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex-shrink-0">
          {label}:
        </span>
        <select
          data-testid={`${testId}-select`}
          value={selectedId}
          onChange={(e) => onSelect(e.target.value)}
          className="flex-1 min-w-0 text-[11px] px-2 py-1 border border-slate-300 rounded bg-white font-mono"
        >
          {drafts.map(d => (
            <option key={d._id} value={d._id}>
              {d.name}{d.is_default ? ' ★' : ''}
            </option>
          ))}
        </select>
        {selectedDraft && (
          <span className="text-[9px] font-mono text-slate-400 truncate hidden xl:inline">
            {selectedDraft.source_roof_key.replace(/_/g, ' ')}
          </span>
        )}
        {numPages > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0" data-testid={`${testId}-pager`}>
            <button
              onClick={() => onPageChange(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
              className="p-0.5 text-slate-500 hover:text-[#316536] disabled:opacity-30"
              title="Previous page"
            ><ChevronLeft size={12} /></button>
            <span className="text-[10px] font-mono text-slate-600">
              {pageNumber}/{numPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
              className="p-0.5 text-slate-500 hover:text-[#316536] disabled:opacity-30"
              title="Next page"
            ><ChevronRight size={12} /></button>
          </div>
        )}
        <button
          data-testid={`${testId}-refresh`}
          onClick={onRefresh}
          className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 border border-[#316536] text-[#316536] rounded hover:bg-[#316536] hover:text-white transition-colors flex-shrink-0"
          title="Refresh this pane"
        >
          <RefreshCw size={10} />
        </button>
      </div>

      {/* Body */}
      {!url ? (
        <div className="flex-1 flex items-center justify-center text-xs text-slate-400">
          Pick a draft to preview…
        </div>
      ) : loadError ? (
        <div className="flex-1 flex flex-col items-center justify-center text-xs text-rose-600 p-6 text-center">
          <div className="font-bold mb-1">Preview failed to load</div>
          <div className="text-slate-500 font-mono text-[10px] max-w-full break-words">{loadError}</div>
        </div>
      ) : (
        <Document
          key={bustKey}
          file={url}
          onLoadSuccess={onDocLoad}
          onLoadError={onDocLoadError}
          options={docOptions}
          loading={<div className="flex-1 flex items-center justify-center text-xs text-slate-400">Rendering PDF…</div>}
          className="flex-1 flex flex-col min-h-0"
        >
          {/* Thumbnail strip */}
          <div className="border-b border-slate-200 bg-slate-100/60 px-2 py-1.5 overflow-x-auto flex-shrink-0" data-testid={`${testId}-thumbs`}>
            <div className="flex gap-1.5 flex-nowrap">
              {Array.from({ length: numPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  data-testid={`${testId}-thumb-${n}`}
                  onClick={() => onPageChange(n)}
                  className={`flex-shrink-0 border-2 rounded overflow-hidden transition-all ${n === pageNumber ? 'border-[#316536] shadow-md' : 'border-slate-300 hover:border-slate-500 opacity-70 hover:opacity-100'}`}
                  title={`Page ${n}`}
                >
                  <div className="pointer-events-none">
                    <Page pageNumber={n} width={54} renderTextLayer={false} renderAnnotationLayer={false} />
                  </div>
                  <div className={`text-center text-[9px] font-mono py-0.5 ${n === pageNumber ? 'bg-[#316536] text-white' : 'bg-white text-slate-500'}`}>{n}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Big page */}
          <div
            className="flex-1 min-h-0 overflow-auto bg-slate-200 flex justify-center p-3"
            data-testid={`${testId}-page-container`}
          >
            {numPages > 0 && (
              <Page
                pageNumber={pageNumber}
                width={520}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-lg bg-white"
              />
            )}
          </div>
        </Document>
      )}
    </div>
  );
}
